import { ReportFilters } from '../types/Report';

export type FilterDimensionType =
  | 'all'
  | 'category'
  | 'brand'
  | 'product'
  | 'customer'
  | 'sales agent'
  | 'territory';

const KEY_TO_TYPE: {
  key: keyof ReportFilters;
  type: Exclude<FilterDimensionType, 'all'>;
}[] = [
  { key: 'categoryIds', type: 'category' },
  { key: 'brandIds', type: 'brand' },
  { key: 'productIds', type: 'product' },
  { key: 'customerIds', type: 'customer' },
  { key: 'territoryIds', type: 'territory' },
  { key: 'salesPersonIds', type: 'sales agent' },
];

const TYPE_TO_KEY: Record<Exclude<FilterDimensionType, 'all'>, keyof ReportFilters> = {
  category: 'categoryIds',
  brand: 'brandIds',
  product: 'productIds',
  customer: 'customerIds',
  territory: 'territoryIds',
  'sales agent': 'salesPersonIds',
};

/** URL / ReportFilters slug for each dimension (sales agent → sales_agent). */
export function filterTypeToSlug(t: FilterDimensionType): string | undefined {
  if (t === 'all') return undefined;
  return t === 'sales agent' ? 'sales_agent' : t;
}

export function slugToFilterType(slug: string): FilterDimensionType | null {
  const s = slug.trim().toLowerCase();
  const map: Record<string, Exclude<FilterDimensionType, 'all'>> = {
    category: 'category',
    brand: 'brand',
    product: 'product',
    customer: 'customer',
    territory: 'territory',
    sales_agent: 'sales agent',
  };
  return map[s] ?? null;
}

function dimensionKeyHasValues(filters: ReportFilters, key: keyof ReportFilters): boolean {
  const v = filters[key];
  return Array.isArray(v) && v.length > 0;
}

/** Order used when inferring primary vs secondary from URL (first two distinct dimensions present). */
const DIMENSION_PRIORITY: (keyof ReportFilters)[] = [
  'categoryIds',
  'brandIds',
  'productIds',
  'customerIds',
  'territoryIds',
  'salesPersonIds',
];

export function filterTypeToReportKey(t: FilterDimensionType): keyof ReportFilters | null {
  if (t === 'all') return null;
  return TYPE_TO_KEY[t];
}

export function reportKeyToFilterType(key: keyof ReportFilters): FilterDimensionType | null {
  const row = KEY_TO_TYPE.find((k) => k.key === key);
  return row ? row.type : null;
}

export function appendReportFiltersToSearchParams(
  filters: ReportFilters,
  params: URLSearchParams = new URLSearchParams()
): URLSearchParams {
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.compareMode) params.set('compareMode', filters.compareMode);
  if (filters.customerIds && filters.customerIds.length > 0) {
    params.set('customerIds', filters.customerIds.join(','));
  }
  if (filters.productIds && filters.productIds.length > 0) {
    params.set('productIds', filters.productIds.map(String).join(','));
  }
  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.set('categoryIds', filters.categoryIds.join(','));
  }
  if (filters.brandIds && filters.brandIds.length > 0) {
    params.set('brandIds', filters.brandIds.join(','));
  }
  if (filters.territoryIds && filters.territoryIds.length > 0) {
    params.set('territoryIds', filters.territoryIds.join(','));
  }
  if (filters.salesPersonIds && filters.salesPersonIds.length > 0) {
    params.set('salesPersonIds', filters.salesPersonIds.join(','));
  }
  if (filters.primary_dimension) {
    params.set('primary_dimension', filters.primary_dimension);
  }
  if (filters.secondary_dimension) {
    params.set('secondary_dimension', filters.secondary_dimension);
  }
  return params;
}

export interface FilterOptionLite {
  id: string | number;
  name: string;
  code?: string;
}

/** Which dimensions have non-empty ID lists, in priority order. */
export function listActiveDimensions(filters: ReportFilters): (keyof ReportFilters)[] {
  const out: (keyof ReportFilters)[] = [];
  for (const key of DIMENSION_PRIORITY) {
    const v = filters[key];
    if (Array.isArray(v) && v.length > 0) {
      out.push(key);
    }
  }
  return out;
}

export function inferPrimarySecondaryKeysFromFilters(filters: ReportFilters): {
  primaryKey: keyof ReportFilters | null;
  secondaryKey: keyof ReportFilters | null;
} {
  const active = listActiveDimensions(filters);
  const p_slug = filters.primary_dimension;
  const s_slug = filters.secondary_dimension;

  if (p_slug) {
    const pt = slugToFilterType(p_slug);
    const pk = pt && pt !== 'all' ? filterTypeToReportKey(pt) : null;
    if (pk && dimensionKeyHasValues(filters, pk)) {
      if (s_slug) {
        const st = slugToFilterType(s_slug);
        const sk = st && st !== 'all' ? filterTypeToReportKey(st) : null;
        if (sk && sk !== pk && dimensionKeyHasValues(filters, sk)) {
          return { primaryKey: pk, secondaryKey: sk };
        }
      }
      if (active.length === 2) {
        const other = active.find((k) => k !== pk);
        if (other) return { primaryKey: pk, secondaryKey: other };
      }
      return { primaryKey: pk, secondaryKey: null };
    }
  }

  if (active.length === 0) return { primaryKey: null, secondaryKey: null };
  if (active.length === 1) return { primaryKey: active[0], secondaryKey: null };
  return { primaryKey: active[0], secondaryKey: active[1] };
}

function idsForKey(filters: ReportFilters, key: keyof ReportFilters): (string | number)[] {
  const v = filters[key];
  if (!Array.isArray(v)) return [];
  return v as (string | number)[];
}

/** Build chip options from one dimension key (e.g. when hydrating secondary on segmented page). */
export function itemsFromFiltersKey(
  filters: ReportFilters,
  key: keyof ReportFilters
): FilterOptionLite[] {
  return idsForKey(filters, key).map((id) => ({ id, name: String(id) }));
}

function dedupeIds(ids: (string | number)[]): (string | number)[] {
  const seen = new Set<string>();
  const out: (string | number)[] = [];
  for (const id of ids) {
    const k = String(id);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(id);
  }
  return out;
}

/**
 * Build ReportFilters from base (dates/compare) + primary/secondary dimension selections.
 * When both rows use the same dimension, IDs are merged with dedupe into one array.
 */
export function buildReportFiltersFromDimensionSelections(
  base: ReportFilters,
  primaryType: FilterDimensionType,
  secondaryType: FilterDimensionType,
  primaryItems: FilterOptionLite[],
  secondaryItems: FilterOptionLite[]
): ReportFilters {
  const next: ReportFilters = {
    startDate: base.startDate,
    endDate: base.endDate,
    compareMode: base.compareMode,
  };

  const setKey = (key: keyof ReportFilters, ids: (string | number)[]) => {
    if (ids.length === 0) return;
    if (key === 'productIds') {
      next.productIds = ids
        .map((id) => (typeof id === 'number' ? id : parseInt(String(id), 10)))
        .filter((n) => !isNaN(n));
    } else {
      (next as Record<string, unknown>)[key] = ids.map((id) => String(id));
    }
  };

  const collect = (t: FilterDimensionType, items: FilterOptionLite[]): (string | number)[] => {
    if (t === 'all' || items.length === 0) return [];
    const key = filterTypeToReportKey(t);
    if (!key) return [];
    return items.map((i) => i.id);
  };

  const pk = filterTypeToReportKey(primaryType);
  const sk = filterTypeToReportKey(secondaryType);

  if (pk && sk && pk === sk) {
    const merged = dedupeIds([
      ...collect(primaryType, primaryItems),
      ...collect(secondaryType, secondaryItems),
    ]);
    setKey(pk, merged);
  } else {
    if (pk) setKey(pk, collect(primaryType, primaryItems));
    if (sk) setKey(sk, collect(secondaryType, secondaryItems));
  }

  delete next.primary_dimension;
  delete next.secondary_dimension;
  if (primaryType !== 'all' && primaryItems.length > 0) {
    const slug = filterTypeToSlug(primaryType);
    if (slug) next.primary_dimension = slug;
  }
  if (secondaryType !== 'all' && secondaryItems.length > 0 && pk && sk && pk !== sk) {
    const sec_slug = filterTypeToSlug(secondaryType);
    if (sec_slug) next.secondary_dimension = sec_slug;
  }

  return next;
}

/** Hydrate selection lists from URL-parsed filters (labels default to id string). */
export function selectionsFromFilters(filters: ReportFilters): {
  primaryType: FilterDimensionType;
  secondaryType: FilterDimensionType;
  primaryItems: FilterOptionLite[];
  secondaryItems: FilterOptionLite[];
} {
  const { primaryKey, secondaryKey } = inferPrimarySecondaryKeysFromFilters(filters);

  const toItems = (key: keyof ReportFilters | null): FilterOptionLite[] => {
    if (!key) return [];
    const raw = idsForKey(filters, key);
    return raw.map((id) => ({
      id,
      name: String(id),
    }));
  };

  return {
    primaryType: primaryKey ? reportKeyToFilterType(primaryKey)! : 'all',
    secondaryType: secondaryKey ? reportKeyToFilterType(secondaryKey)! : 'all',
    primaryItems: toItems(primaryKey),
    secondaryItems: toItems(secondaryKey),
  };
}
